import React, { FunctionComponent } from 'react';
import {
  PriceRuleOffsetType,
  PriceRuleSource,
} from '@upstart/harmony/lib/constants';
import { PriceRule } from '@upstart/harmony/lib/types/dealerTypes';
import { DealerLaunchStepProps } from '../types';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { DEFAULT_PRICE_SOURCE } from '../defaults';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import { Radio } from '../../Input/Radio/Radio';
import {
  getNewInstorePriceSource,
  getUsedInstorePriceSource,
} from '../../../utils/pricing';
import { PRICE_SOURCE_NAME } from '../../../utils/constants';

interface FormData {
  priceSource: PriceRuleSource;
  usedPriceSource: PriceRuleSource;
}

export const DealerLaunchPricingSource: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const form = useLaunchpadForm<FormData>({
    defaultValues: {
      // this step does not support different client types or conditions, so take from instore.New and overwrite the rest later
      priceSource: getNewInstorePriceSource(dealer) || DEFAULT_PRICE_SOURCE,
      usedPriceSource:
        getUsedInstorePriceSource(dealer) || DEFAULT_PRICE_SOURCE,
    },
  });

  const onSubmit = async (data: FormData) => {
    const priceRule: PriceRule = {
      priceSource: data.priceSource,
      offsetType: PriceRuleOffsetType.ADD,
      offsetValue: undefined,
    };

    const usedPriceRule: PriceRule = {
      priceSource: data.usedPriceSource,
      offsetType: PriceRuleOffsetType.ADD,
      offsetValue: undefined,
    };

    // overwrite all the default price rules for different client types and conditions
    await nextStep({
      priceRules: {
        instore: {
          New: { defaultRule: priceRule },
          Used: { defaultRule: usedPriceRule },
        },
        online: {
          New: { defaultRule: priceRule },
          Used: { defaultRule: usedPriceRule },
        },
      },
    });
  };

  return (
    <DealerLaunchFormCardLayout
      form={form}
      title="Now, let's look at how vehicles are priced"
      subTitle="We need to know which price that you use from your inventory feed to price your vehicles so that we're always showing the correct price to your customers."
      cardTitle="Which source do you use to set your pricing?"
      prevStep={prevStep}
      onFormSubmit={onSubmit}
    >
      {!dealer.isIndependent && (
        <span>
          <h2>New cars</h2>
          <Radio
            className="flex-column"
            name="priceSource"
            options={[
              {
                label: PRICE_SOURCE_NAME[PriceRuleSource.MSRP],
                value: PriceRuleSource.MSRP,
              },
              {
                label: PRICE_SOURCE_NAME[PriceRuleSource.INVOICE],
                tooltip: 'Invoice Price from your inventory feed.',
                value: PriceRuleSource.INVOICE,
              },
              {
                label: PRICE_SOURCE_NAME[PriceRuleSource.FEED_PRICE],
                tooltip: 'Internet price from your inventory feed.',
                value: PriceRuleSource.FEED_PRICE,
              },
            ]}
            form={form}
          />
        </span>
      )}
      <h2>Used cars</h2>
      <Radio
        className="flex-column"
        name="usedPriceSource"
        options={[
          {
            label: PRICE_SOURCE_NAME[PriceRuleSource.INVOICE],
            tooltip: 'Invoice Price from your inventory feed.',
            value: PriceRuleSource.INVOICE,
          },
          {
            label: PRICE_SOURCE_NAME[PriceRuleSource.FEED_PRICE],
            tooltip: 'Internet price from your inventory feed.',
            value: PriceRuleSource.FEED_PRICE,
          },
        ]}
        form={form}
      />
    </DealerLaunchFormCardLayout>
  );
};
